// Pure helpers for building the save-route-plan / route_options payload from
// computed route candidates. Extracted from DispatcherBoard so the GM3A
// geometry/trip_plan binding invariant can be unit-tested in isolation.
//
// GM3A invariant: the trip_plan persisted with a route's geometry MUST be the
// plan produced FROM that geometry. Each plan-full-trip result is bound to ITS
// OWN route option by POSITION (pairs[i] <-> routes[i]), never recovered later
// through the reused generic route_type label ("route_0"/"route_1"/"toll_free"),
// which repeats across routes and loads and previously let one route's fuel plan
// (computed from a different geometry) attach to another route's geometry -- the
// AZ->CA-geometry + Odessa-TX-fuel-stops defect.

export function attachTripPlans(routes, pairs) {
  // pairs[i] == [route_type, planData] aligned by index to routes[i].
  return (routes || []).map((opt, i) => ({
    ...opt,
    trip_plan: pairs && pairs[i] ? pairs[i][1] : null,
  }));
}

export function buildRouteOptionsPayload(routes, _tripPlanByType, rateForProfit) {
  return (routes || []).map((opt) => {
    // Read the plan from the route object itself (bound by position via
    // attachTripPlans); the 2nd arg (formerly tripPlanByType, keyed by the
    // reused route_type) is intentionally ignored now.
    const tripPlan = opt.trip_plan || null;
    const durationHours = tripPlan?.total_drive_hours ?? opt.driving_hours ?? null;
    const totalCost = opt.total_cost != null ? Number(opt.total_cost) : null;
    const hasWaitData = opt.wait_hours != null && opt.wait_cost != null;
    const waitHours = hasWaitData ? Number(opt.wait_hours) : null;
    const waitCost = hasWaitData ? Number(opt.wait_cost) : null;
    const profitAfterWait =
      rateForProfit != null && totalCost != null
        ? Number(rateForProfit) - totalCost
        : opt.profit_if_offered != null
        ? Number(opt.profit_if_offered)
        : null;
    const contributionProfit =
      hasWaitData && profitAfterWait != null ? profitAfterWait + waitCost : profitAfterWait;
    return {
      route_type: opt.route_type,
      distance_miles: opt.distance_miles,
      duration_hours: durationHours,
      toll_cost: opt.toll_cost,
      fuel_cost: opt.fuel_cost,
      driver_cost: opt.driver_cost,
      other_cost: opt.other_cost,
      insurance_cost: opt.insurance_cost,
      office_payroll_cost: opt.office_payroll_cost,
      maintenance_cost: opt.maintenance_cost,
      total_cost: totalCost,
      contribution_profit: contributionProfit,
      wait_hours: waitHours,
      wait_cost: waitCost,
      profit_after_wait: hasWaitData ? profitAfterWait : null,
      deadhead_miles: opt.deadhead_miles,
      loaded_miles: opt.loaded_miles,
      geometry: opt.geometry,
      toll_count: opt.toll_stops?.length ?? opt.toll_details?.toll_count ?? 0,
      toll_names: opt.toll_stops?.map((t) => t.name) ?? opt.toll_details?.toll_names ?? [],
      toll_stops: opt.toll_stops || opt.toll_details?.toll_stops || [],
      trip_plan: tripPlan,
    };
  });
}
